import type { Edge, Edge as ReactFlowEdge } from "reactflow";
import { isPresent } from "ts-extras";
import type { ExperimentStatus } from "@hightouch/lib/experiments/types";
import { readWhatsAppEditorContent } from "@hightouch/lib/cep/types/whatsapp-template";
import type { FMRelationshipConfig } from "@hightouch/lib/customer-data/decision-engine/types";

import type {
  DestinationInstancesSetInput,
  EdgeInput,
  JourneyEdges,
  JourneyGraphInput,
  JourneyQuery,
  JourneyTemplateInput,
  MessageConfigInput,
  NodeInput,
  NoteInput,
} from "src/graphql";
import {
  EditorType,
  type RenderedOutput,
  whatsAppRenderedOutput,
} from "src/cep/messages/types";
import { mergeMessageContent } from "src/cep/messages/merge-message-content";
import {
  type ConfiguredSyncConfigDetails,
  type EphemeralSyncConfigDetails,
  isEphemeralSyncConfig,
  isPlaceholderSyncConfig,
  isStagedJourney,
  type JourneyGraph,
  type JourneyMessageConfig,
  type JourneyNoteNode,
  type PlaceholderSyncConfigDetails,
  type StagedJourneyGraph,
  type ExecutableJourneyNode,
  type JourneyNoteDetails,
  type JourneyCanvasNote,
} from "src/pages/journeys/types";
import { inAppContentFromMessageConfig } from "src/pages/journeys/forms/messages/in-app-utils";
import { SUBJECT_ELEMENT } from "src/pages/journeys/nodes/node-ab-test-summary";
import type { SyncWizardOutput } from "src/pages/syncs/create/types";
import { ChannelType } from "src/types/cep";
import {
  type DecisionOutcome,
  type JourneyContextColumn,
  type JourneyContextVariableDefinition,
  type JourneyNodeSyncMode,
  type JourneyNodeType,
  NOTE_NODE_TYPE,
} from "src/types/journeys";

import { omit } from "lodash";

import { omitMessageContentFields } from "./message-content-fields";

type JourneyNode = NonNullable<
  JourneyQuery["journeys"][number]["nodes"][number]
>;

const buildDecisionOutcomeFromNode = (
  node: Omit<JourneyNode, "position">,
): DecisionOutcome | null => {
  const outcome =
    node.decision_engine_flow_links?.[0]?.flow?.outcomes?.[0]?.outcome;
  if (!outcome) {
    return null;
  }

  return {
    segment_id: Number(outcome.segment_id),
    filter: outcome.filter ?? null,
  };
};

type StoredColumn = FMRelationshipConfig["columns"][number];

const isStoredColumn = (column: unknown): column is StoredColumn =>
  typeof column === "object" &&
  column !== null &&
  "name" in column &&
  typeof column.name === "string";

// Mirrors loadFeatureMatrixContextColumns: an empty selection is stored as the uncast primary key alone.
const isPrimaryKeyPlaceholder = (
  columns: StoredColumn[],
  primaryKey: string | null | undefined,
) => {
  const [onlyColumn, ...rest] = columns;
  return (
    onlyColumn !== undefined &&
    rest.length === 0 &&
    onlyColumn.name === primaryKey &&
    onlyColumn.castTo == null &&
    !onlyColumn.aggregations?.length &&
    !onlyColumn.filters?.length
  );
};

const buildStoredDecisionContextColumnsFromNode = (
  node: Omit<JourneyNode, "position">,
): JourneyContextColumn[] => {
  const matrix =
    node.decision_engine_flow_links?.[0]?.flow?.decision_engine_feature_matrix;
  return (matrix?.relationships ?? []).flatMap(({ config }) => {
    const columns: unknown = config?.columns;
    if (!Array.isArray(columns)) {
      return [];
    }
    const storedColumns = columns.filter(isStoredColumn);
    return isPrimaryKeyPlaceholder(
      storedColumns,
      matrix?.parent_model?.primary_key,
    )
      ? []
      : storedColumns.map((column) => ({
          name: column.name,
          castTo: column.castTo ?? null,
        }));
  });
};

// A WhatsApp message's content rows leave editor_state empty; its template's locale row holds the content and category.
const buildWhatsAppContentFromTemplate = (message: JourneyMessage) => {
  const template =
    message.whatsapp_template_content?.[0]?.cep_whatsapp_template;
  if (!template) return null;
  const locale = template.cep_whatsapp_template_locales[0];
  const content = readWhatsAppEditorContent(locale?.editor_state);
  return {
    editor_state: { editorType: EditorType.Whatsapp, editorState: content },
    rendered_output: whatsAppRenderedOutput(content),
    whatsapp_category: locale?.requested_category ?? null,
  };
};

const buildMessageConfigFromNode = (node: Omit<JourneyNode, "position">) => {
  const campaignMessage = node.campaign_messages?.[0];
  if (!campaignMessage?.message) {
    return node.config.message_config ?? null;
  }

  const msg = campaignMessage.message;
  const content = mergeMessageContent(
    msg.current_content,
    msg.current_metadata,
  );
  const whatsAppContent =
    node.config.message_type === ChannelType.Whatsapp
      ? buildWhatsAppContentFromTemplate(msg)
      : null;

  return {
    message_id: msg.id,
    messagePersisted: true,
    cep_brand_id: msg.cep_brand_id,
    cep_device_preview_id: msg.cep_device_preview_id,
    name: msg.name,
    subject: content?.subject,
    preheader: content?.preheader,
    editor_state: whatsAppContent?.editor_state ?? content?.editor_state,
    rendered_output:
      whatsAppContent?.rendered_output ??
      (content?.rendered_output as RenderedOutput) ??
      null,
    content_dynamic_slot_usages: msg.content_dynamic_slot_usages,
    profile_mappings: content?.profile_mappings ?? [],
    ...(whatsAppContent && {
      whatsapp_category: whatsAppContent.whatsapp_category,
    }),
    sender_identity_id: msg.cep_sender_identity?.id ?? null,
    send_gates: msg.send_gates,
    transactional: msg.transactional,
    subscription_topic_id: msg.cep_subscription_topic?.id ?? null,
    reply_to_name:
      msg.send_config?.reply_to_name ??
      node.config.message_config?.reply_to_name ??
      null,
    reply_to_email:
      msg.send_config?.reply_to_email ??
      node.config.message_config?.reply_to_email ??
      null,
    tags: msg.tags,
    message_push_apps: msg.message_push_apps,
    experiments: buildExperimentsFromMessage(msg),
    startedExperimentElements: (msg.message_experiments ?? [])
      .filter((experiment) => experiment.experiment?.started_at != null)
      .map((experiment) => experiment.element),
    promotedVariantKeys: Object.fromEntries(
      (msg.message_experiments ?? []).flatMap((experiment) =>
        experiment.promoted_variant_key == null
          ? []
          : [[experiment.element, experiment.promoted_variant_key]],
      ),
    ),
  };
};

type JourneyMessage = NonNullable<
  NonNullable<JourneyNode["campaign_messages"]>[number]["message"]
>;

type HydratedExperiment = NonNullable<
  JourneyMessage["message_experiments"]
>[number];

// Typed off the query so dropping experiment_metrics from the selection breaks
// the build instead of quietly reading as "no metric".
export const buildPrimaryMetric = (
  metrics:
    | NonNullable<HydratedExperiment["experiment"]>["experiment_metrics"]
    | undefined,
) => {
  const primary = metrics?.[0];
  // Absent, never null — the resolver reads a present null as "clear the metric".
  if (!primary || primary.goal_definition == null) return undefined;
  return {
    goal_definition: primary.goal_definition,
    direction: primary.direction ?? null,
  };
};

// The stored secondary metrics, in a stable order. Carried so a load → save
// round-trip preserves the user's set (the resolver treats the array as the
// full set and reconciles to it). Absent leaves the stored secondaries alone.
const buildSecondaryMetrics = (
  metrics:
    | NonNullable<HydratedExperiment["experiment"]>["secondary_metrics"]
    | undefined,
) =>
  metrics?.map((metric) => ({
    goal_definition: metric.goal_definition,
    direction: metric.direction ?? null,
  }));

// Hasura message_experiments → the in-memory experiments shape (matching
// MessageExperimentInput), so A/B tests live in the graph and round-trip on save.
const buildExperimentsFromMessage = (msg: JourneyMessage) =>
  (msg.message_experiments ?? []).map((experiment) => ({
    element: experiment.element,
    name: experiment.experiment?.name ?? null,
    measurement_config: experiment.experiment?.measurement_window ?? null,
    // Carried so a load → save round-trip does not clear the stored metric.
    primary_metric: buildPrimaryMetric(
      experiment.experiment?.experiment_metrics,
    ),
    secondary_metrics: buildSecondaryMetrics(
      experiment.experiment?.secondary_metrics,
    ),
    arms: (experiment.arms ?? []).map((arm) => ({
      variant_key: arm.variant_key,
      // The save matches arms by this id, so an arm loaded without it is replaced by a new one.
      variant_id:
        (experiment.element === SUBJECT_ELEMENT
          ? arm.metadata_contents?.variant_id
          : arm.body_contents?.variant_id) ?? null,
      name: arm.name ?? null,
      percentage: arm.percentage ?? null,
      editor_state: arm.body_contents?.editor_state ?? null,
      rendered_output: arm.body_contents?.rendered_output ?? null,
      profile_mappings:
        arm.body_contents?.profile_mappings ??
        arm.metadata_contents?.profile_mappings ??
        null,
      subject: arm.metadata_contents?.subject ?? null,
      preheader: arm.metadata_contents?.preheader ?? null,
    })),
  }));

export const transformNodeToReactFlowNode = ({
  position,
  ...node
}: JourneyNode): ExecutableJourneyNode => {
  return {
    id: node.id,
    type: node.config.type as JourneyNodeType,
    position,
    data: {
      ...node,
      experiment: node.experiment
        ? {
            ...node.experiment,
            status: (node.experiment.status as ExperimentStatus) ?? undefined,
          }
        : null,
      message_config: buildMessageConfigFromNode(node),
      decision_outcome: buildDecisionOutcomeFromNode(node),
      stored_decision_context_columns:
        buildStoredDecisionContextColumnsFromNode(node),
      sync_configs: node.sync_configs?.filter(isPresent).map((syncConfig) => {
        if (isPlaceholderSyncConfig(syncConfig)) {
          return {
            ...syncConfig,
            // This is a NUMBER but the type says string
            placeholder_destination_id: Number(
              syncConfig.placeholder_destination_id,
            ),
          } satisfies PlaceholderSyncConfigDetails;
        }

        if (isEphemeralSyncConfig(syncConfig)) {
          // "Ephemeral" sync configs represent syncs that are not persisted/live in the database. The only time we can have
          // that value here is if we're working with journey templates.
          return {
            ...syncConfig,
            mode: syncConfig.mode as JourneyNodeSyncMode,
            destination_instance_id: null,
            exit_config: {
              // UI needs these values to be `null`. `undefined` does not work with radio buttons.
              remove_after: syncConfig.exit_config.remove_after ?? null,
              // UI needs these values to be `null`. `undefined` does not work with radio buttons.
              remove_on_journey_exit:
                syncConfig.exit_config.remove_on_journey_exit ?? null,
              // Trigger-only.
              expire_after: syncConfig.exit_config.expire_after ?? null,
              // Message-only.
              wait_for: syncConfig.exit_config.wait_for ?? null,
              on_timeout: syncConfig.exit_config.on_timeout ?? null,
              send_in_run: syncConfig.exit_config.send_in_run ?? null,
            },
            // This is a NUMBER but the type says string
            placeholder_destination_id: syncConfig.placeholder_destination_id
              ? Number(syncConfig.placeholder_destination_id)
              : null,
            // The sync was configured on a previous save of the template.
            full_sync_config: syncConfig.full_sync_config,
          } satisfies EphemeralSyncConfigDetails;
        }

        return {
          ...syncConfig,
          mode: syncConfig.mode as JourneyNodeSyncMode,
          // This is a NUMBER but the type says string
          destination_instance_id: Number(syncConfig.destination_instance_id),
          exit_config: {
            // UI needs these values to be `null`. `undefined` does not work with radio buttons.
            remove_after: syncConfig.exit_config.remove_after ?? null,
            // UI needs these values to be `null`. `undefined` does not work with radio buttons.
            remove_on_journey_exit:
              syncConfig.exit_config.remove_on_journey_exit ?? null,
            // Trigger-only.
            expire_after: syncConfig.exit_config.expire_after ?? null,
            // Message-only.
            wait_for: syncConfig.exit_config.wait_for ?? null,
            on_timeout: syncConfig.exit_config.on_timeout ?? null,
            send_in_run: syncConfig.exit_config.send_in_run ?? null,
          },
          // This is a NUMBER but the type says string
          placeholder_destination_id: syncConfig.placeholder_destination_id
            ? Number(syncConfig.placeholder_destination_id)
            : null,
          // `null` is the default for full_sync_config. Only when creating or updating syncs does this get populated
          full_sync_config: null,
        } satisfies ConfiguredSyncConfigDetails;
      }),
    },
  };
};

export const transformEdgeToReactFlowEdge = (
  edge: Pick<JourneyEdges, "from_node_id" | "to_node_id">,
): ReactFlowEdge => {
  return {
    id: `${edge.from_node_id}-${edge.to_node_id}`,
    source: edge.from_node_id,
    target: edge.to_node_id,
    sourceHandle: null,
    targetHandle: null,
  };
};

export const transformNoteToReactFlowNode = ({
  position,
  ...note
}: JourneyCanvasNote): JourneyNoteNode => {
  return {
    id: note.id,
    type: NOTE_NODE_TYPE,
    position,
    data: note,
  };
};

/**
 * Expands the note metadata information to be easier to work with in the graph.
 */
export const transformNoteToGraphNote = ({
  created_by_user,
  updated_by_user,
  created_at,
  updated_at,
  ...note
}: NonNullable<
  JourneyQuery["journeys"][number]["notes"][number]
>): JourneyNoteDetails => {
  return {
    ...note,
    created_by: created_by_user?.name ?? null,
    updated_by: updated_by_user?.name ?? null,
    created_at: created_at ?? null,
    updated_at: updated_at ?? null,
  };
};

const transformFullSyncConfigToSyncInput = (
  input: SyncWizardOutput | null,
): DestinationInstancesSetInput | null => {
  if (!input) {
    return null;
  }

  const {
    segment: _segment,
    destination,
    sync_template_id,
    ...syncConfig
  } = input;
  const syncInput: DestinationInstancesSetInput = {
    ...syncConfig,
    // Do not include segment_id. The backend will set that.
    destination_id: destination.id,
  };
  // An explicit `null` means the sync was detached from its template this
  // session and must clear the link on save.
  if (sync_template_id !== undefined) {
    syncInput.sync_template_id =
      sync_template_id === null ? null : Number(sync_template_id);
  }
  return syncInput;
};

// JourneyMessageConfig carries a UI-only `message_push_apps` field with the
// nested push-app relationship rows used for display. The backend's
// MessageConfigInput knows nothing about it — push_app_ids is the wire field
// and the wizard sets it directly via formStateToMessageConfig. Strip the UI
// overlay so the save mutation doesn't get rejected; leave push_app_ids
// untouched so "user didn't edit push apps" stays as undefined (backend then
// preserves existing associations).
//
// Content fields ride the same "undefined = preserve" convention: for a
// row-backed node with no unsaved content edits this session
// (`includeContent === false`), drop them so the backend skips its
// `message_contents` UPDATE — the node's load-snapshot content would otherwise
// overwrite a fresher messages-area edit on every journey save. Nodes without
// a backing row always send content: the save is what materializes the row.
// The dropped set is `MESSAGE_CONTENT_FIELDS` — content-writing persist paths
// must go through `persistMessageContentToNode` so the node gets marked.
//
// `contentDirtyNodeIds === undefined` means "include all content". The
// template payload path (`transformJourneyGraphToTemplatePayload`) relies on
// this: `journey_templates.definition` stores the whole graph as JSON and IS
// the template's content store — no `messages`/`message_contents` rows exist
// behind it, so omitting "clean" nodes' content there would drop it from the
// template entirely.
export const transformMessageConfigToInput = (
  messageConfig: JourneyMessageConfig | null,
  contentDirtyNodeIds?: ReadonlySet<string>,
  nodeId?: string,
): MessageConfigInput | null => {
  if (!messageConfig) return null;
  const rest = omit(messageConfig, [
    "message_push_apps",
    "startedExperimentElements",
    "promotedVariantKeys",
    "messagePersisted",
    // The tile keeps the message's saved usage rows for its previews, and the wire has no field for them.
    "content_dynamic_slot_usages",
  ]);
  const includeContent =
    messageConfig.messagePersisted !== true ||
    contentDirtyNodeIds == null ||
    (nodeId != null && contentDirtyNodeIds.has(nodeId));
  if (includeContent) {
    // Derived here so every buffer holding an in-app instance saves it, a duplicated tile's included.
    const inAppContent = inAppContentFromMessageConfig(messageConfig);
    return inAppContent
      ? {
          ...rest,
          inapp_content: {
            template_id: inAppContent.templateId,
            values: inAppContent.values,
          },
        }
      : rest;
  }
  return omitMessageContentFields(rest);
};

const transformJourneyNodesToNodeInput = (
  node: ExecutableJourneyNode,
  opts: {
    isTemplateContext?: boolean;
    contentDirtyNodeIds?: ReadonlySet<string>;
  } = { isTemplateContext: false },
): NodeInput => {
  const { isTemplateContext = false, contentDirtyNodeIds } = opts;
  // TODO: For split nodes, format the experiment data (if it's attached)
  // and add it to the node input
  return {
    id: node.data.id,
    name: node.data.name,
    description: node.data.description || null,
    // Make sure to cast as strings here.
    segment_id: node.data.segment_id?.toString() ?? null,
    // Make sure to cast as strings here.
    event_relationship_id: node.data.event_relationship_id?.toString() ?? null,
    type: node.data.config.type,
    // Realtime sync nodes carry one managed subscription instead of batch
    // sync_configs; the backend rejects a node that sends both.
    realtime_sync_config: node.data.realtime_sync_config ?? undefined,
    realtime_function_config: node.data.realtime_function_config ?? undefined,
    sync_configs: node.data.realtime_sync_config
      ? undefined
      : node.data.sync_configs?.map((syncConfig) => {
          if (isPlaceholderSyncConfig(syncConfig)) {
            return {
              ...syncConfig,
              mode: isTemplateContext ? "placeholder-mode" : undefined,
              placeholder_destination_id:
                syncConfig.placeholder_destination_id.toString(),
            };
          }

          return {
            ...syncConfig,
            destination_instance_id:
              syncConfig.destination_instance_id?.toString() ?? null,
            exit_config: {
              // Backend does not allow `null` values.
              remove_after: syncConfig.exit_config.remove_after ?? undefined,
              // Backend does not allow `null` values.
              remove_on_journey_exit:
                syncConfig.exit_config.remove_on_journey_exit ?? undefined,
              // Trigger-only. Backend does not allow `null` values.
              expire_after: syncConfig.exit_config.expire_after ?? undefined,
              // Message-only. Backend does not allow `null` values.
              wait_for: syncConfig.exit_config.wait_for ?? undefined,
              on_timeout: syncConfig.exit_config.on_timeout ?? undefined,
              send_in_run: syncConfig.exit_config.send_in_run ?? undefined,
            },
            placeholder_destination_id:
              syncConfig.placeholder_destination_id?.toString(),
            full_sync_config: isTemplateContext
              ? // Templates do not require any transformation and always store updates to the journey_templates table.
                syncConfig.full_sync_config
              : transformFullSyncConfigToSyncInput(syncConfig.full_sync_config),
          };
        }),
    message_config: transformMessageConfigToInput(
      node.data.message_config,
      contentDirtyNodeIds,
      node.data.id,
    ),
    config: {
      ...node.data.config,
    },
    decision_outcome: node.data.decision_outcome
      ? {
          segment_id: String(node.data.decision_outcome.segment_id),
          filter: node.data.decision_outcome.filter,
        }
      : null,
    decision_context_columns: node.data.decision_context_columns,
    position: node.position,
  };
};

const transformNoteToNoteInput = (note: JourneyNoteDetails): NoteInput => {
  return {
    id: note.id,
    content: note.content,
    position: note.position ?? null,
    node_id: note.node_id ?? null,
  };
};

const transformJourneyEdgesToEdgeInput = (node: Edge): EdgeInput => {
  return {
    from_node_id: node.source,
    to_node_id: node.target,
  };
};

const serializeExitCriteria = (
  exitCriteria: JourneyGraph["journey"]["exitCriteria"],
) => {
  // IMPORTANT: { type: ConditionType.And/Or, conditions: [] } exits _everybody_.
  // Value must be `null` to clear them out. Event-only exits keep that empty
  // conditions tree, so they still need to persist.
  const hasCriteriaConditions = exitCriteria.conditions.length > 0;
  const hasEventExit =
    exitCriteria.exit_method === "event" && exitCriteria.event_model_id != null;

  return hasCriteriaConditions || hasEventExit ? exitCriteria : null;
};

export const transformJourneyGraphToPayload = (
  data: JourneyGraph | StagedJourneyGraph,
  opts?: { contentDirtyNodeIds?: ReadonlySet<string> },
): JourneyGraphInput => {
  return {
    journey: {
      id: data.journey.id,
      // IMPORTANT: version_id will ONLY be populated when working on a staged graph
      // - Staged context: specific version ID (enables version-specific editing)
      // - Regular context: omitted (backend uses latest version, no version validation)
      ...(isStagedJourney(data) && {
        version_id: data.journey.version_id,
      }),
      name: data.journey.name,
      archived: false, // archival was removed, so always set this to false for now.
      // TODO(samuel): Graphql type is incorrect. null should be allowed but default to in string for now
      description: data.journey.description ?? "",
      status: data.journey.status,
      schedule: data.journey.schedule,
      context_config: transformContextConfigToContextConfigInput(
        data.journey.contextConfig,
      ),
      exit_criteria: serializeExitCriteria(data.journey.exitCriteria),
      notes: data.notes.map((note) => transformNoteToNoteInput(note)),
    },
    nodes: data.nodes.map((node) =>
      transformJourneyNodesToNodeInput(node, {
        contentDirtyNodeIds: opts?.contentDirtyNodeIds,
      }),
    ),
    edges: data.edges.map(transformJourneyEdgesToEdgeInput),
  };
};

export const transformJourneyGraphToTemplatePayload = (
  data: JourneyGraph | StagedJourneyGraph,
): JourneyTemplateInput => {
  return {
    id: data.journey.id,
    name: data.journey.name,
    description: data.journey.description ?? "",
    journeyGraph: {
      journey: {
        id: data.journey.id,
        name: data.journey.name,
        description: data.journey.description ?? "",
        schedule: data.journey.schedule,
        exit_criteria: serializeExitCriteria(data.journey.exitCriteria),
        notes: data.notes.map((note) => transformNoteToNoteInput(note)),
        context_config: transformContextConfigToContextConfigInput(
          data.journey.contextConfig,
        ),
      },
      nodes: data.nodes.map((node) =>
        transformJourneyNodesToNodeInput(node, {
          isTemplateContext: true,
        }),
      ),
      edges: data.edges.map(transformJourneyEdgesToEdgeInput),
    },
  };
};

function transformContextConfigToContextConfigInput(
  contextConfig: JourneyGraph["journey"]["contextConfig"],
): { variables: JourneyContextVariableDefinition[] } | null {
  const variables = contextConfig.variables.map((v) => ({
    ...v,
    // Lowercase variable names to prevent capitalization collisions
    variable_name: v.variable_name.toLowerCase(),
  }));

  // { variables: [] } is transformed to be null. An empty array means no variables, which should be represented as null.
  return variables.length === 0 ? null : { variables };
}
